import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import API from 'core/Api';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { useModal } from 'store/modal';

import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';

import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import CreateEntity from 'components/crud/CreateEntity';
import { ISelectOptionProps } from 'components/form/types';
import { createErrorFromResponse } from 'core/Error';

interface IUserOption extends ISelectOptionProps {
  groups?: number[];
  status?: string;
}

interface ICountryOption {
  id: string;
  name: string;
  daily_allowance_eur?: number;
  accommodation_limit_eur?: number;
}

interface IAccommodationOvernightStaysTypeOptions {
  domestic?: ISelectOptionProps[];
  abroad?: ISelectOptionProps[];
}

interface IDropdowns {
  user?: IUserOption[];
  dayly_allowance_payment_type?: ISelectOptionProps[];
  accommodation_payment_type?: ISelectOptionProps[];
  accommodation_overnight_stays_type?: IAccommodationOvernightStaysTypeOptions;
  travel_payment_type?: ISelectOptionProps[];
  transport_type?: ISelectOptionProps[];
  countries?: ICountryOption[];
}

const ACCOMMODATION_OVERNIGHT_STAYS_TYPE_VALUES = {
  domestic: {
    noOvernightStays: 'accommodation_no_overnight_stays',
    default: 'accommodation_by_document',
    documented: 'accommodation_by_document',
    documentedWithLimit: 'accommodation_by_document_with_limit',
  },
  abroad: {
    noOvernightStays: 'accommodation_no_overnight_stays_abroad',
    default: 'accommodation_by_law_documented_abroad',
    documented: 'accommodation_in_actual_size_abroad',
    documentedWithLimit: 'accommodation_by_law_documented_abroad',
  },
};

interface IInputs {
  user_id: IUserOption | null;
  trip_scope: string;
  country: ISelectOptionProps | null;
  destination: string;
  route: string;
  from_date: Date | null;
  to_date: Date | null;
  trip_days: number;
  number_of_overnight_stays: number;
  is_standard_daily_allowance: string;
  daily_allowance: number;
  task: string;
  transport_type: ISelectOptionProps | null;
  transport_additional_details: string;
  daily_allowance_payment_type: string;
  accommodation_overnight_stays_type: string;
  accommodation_payment_type: string;
  travel_payment_type: string;
  accommodation_amount: number;
  travel_amount: number;
}

const getDifferenceInDays = (fromDate: Date | null, toDate: Date | null): number => {
  if (!fromDate || !toDate) return 0;

  // Normalize both dates to midnight - from_date/to_date come from independent
  // datepicker selections and can carry different, non-zero times of day
  // (e.g. the time each field happened to be picked at), which throws off a
  // raw ms subtraction by up to a day (0 days for a same-day trip, 1 day short
  // for a 2-day trip, etc.) even though the calendar dates themselves are correct.
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(0, 0, 0, 0);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;

  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

const translateApiLabel = (t: (key: string) => string, label?: string): string => {
  if (!label) return '';
  if (label.startsWith('work_trips.')) {
    return t(label);
  }
  return label;
};

const CreateForm: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { language } = useLanguageContext();
  const { client } = useActiveClientContext();
  const { showModal } = useModal();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<IInputs>({
    defaultValues: {
      user_id: null,
      trip_scope: 'domestic',
      country: null,
      destination: '',
      route: '',
      from_date: null,
      to_date: null,
      trip_days: 0,
      number_of_overnight_stays: 0,
      is_standard_daily_allowance: 'yes',
      daily_allowance: 0,
      task: '',
      transport_type: null,
      transport_additional_details: '',
      daily_allowance_payment_type: 'bank',
      accommodation_overnight_stays_type: 'accommodation_no_overnight_stays',
      accommodation_payment_type: 'bank',
      travel_payment_type: 'bank',
      accommodation_amount: 0,
      travel_amount: 0,
    },
  });

  const fromDate = methods.watch('from_date');
  const toDate = methods.watch('to_date');
  const tripScope = methods.watch('trip_scope');
  const selectedCountry = methods.watch('country');
  const isStandardDailyAllowance = methods.watch('is_standard_daily_allowance') === 'yes';
  const overnightStays = Number(methods.watch('number_of_overnight_stays') || 0);
  const accommodationOvernightStaysType = methods.watch('accommodation_overnight_stays_type');

  const { data: dropdowns } = useQuery<IDropdowns, Error>(
    [buildClientEntity('work-trips-create-dropdowns', client)],
    async (): Promise<IDropdowns> => {
      const res = await API.get('work-trips/dropdown-options?all-users=true');
      return res.data?.[language || 'bg'] || {};
    },
    {
      cacheTime: 0,
    }
  );

  const tripDays = useMemo(() => getDifferenceInDays(fromDate, toDate), [fromDate, toDate]);

  const countries = dropdowns?.countries || [];

  const bgCountry = useMemo(() => countries.find(country => country.id === 'BG') || countries[0], [countries]);

  const countryOptions = useMemo<ISelectOptionProps[]>(
    () =>
      countries
        .filter(country => tripScope !== 'foreign' || country.id !== 'BG')
        .map(country => ({
          value: country.id,
          label: country.name,
        })),
    [countries, tripScope]
  );

  const transportTypes = dropdowns?.transport_type || [];

  const companyCar = useMemo(
    () => transportTypes.find(transport => transport.value === 'company_car') || transportTypes[0],
    [transportTypes]
  );

  const transportOptions = useMemo(
    () =>
      (dropdowns?.transport_type || []).map(option => ({
        ...option,
        label: translateApiLabel(t, option.label),
      })),
    [dropdowns, t]
  );

  const daylyAllowancePaymentTypeOptions = useMemo(
    () =>
      (dropdowns?.dayly_allowance_payment_type || []).map(option => ({
        ...option,
        label: translateApiLabel(t, option.label),
      })),
    [dropdowns, t]
  );

  const accommodationPaymentTypeOptions = useMemo(
    () =>
      (dropdowns?.accommodation_payment_type || []).map(option => ({
        ...option,
        label: translateApiLabel(t, option.label),
      })),
    [dropdowns, t]
  );

  const travelPaymentTypeOptions = useMemo(
    () =>
      (dropdowns?.travel_payment_type || []).map(option => ({
        ...option,
        label: translateApiLabel(t, option.label),
      })),
    [dropdowns, t]
  );

  const accommodationOvernightStaysTypeValues =
    tripScope === 'foreign'
      ? ACCOMMODATION_OVERNIGHT_STAYS_TYPE_VALUES.abroad
      : ACCOMMODATION_OVERNIGHT_STAYS_TYPE_VALUES.domestic;

  const accommodationOvernightStaysTypeOptions = useMemo(
    () =>
      (
        (tripScope === 'foreign'
          ? dropdowns?.accommodation_overnight_stays_type?.abroad
          : dropdowns?.accommodation_overnight_stays_type?.domestic) || []
      ).map(option => ({
        ...option,
        label: translateApiLabel(t, option.label),
      })),
    [dropdowns, tripScope, t]
  );

  const selectedCountryData = useMemo(
    () => countries.find(country => country.id === selectedCountry?.value),
    [countries, selectedCountry]
  );

  const baseDailyAllowanceByCountry = Number(selectedCountryData?.daily_allowance_eur || 0);
  const standardDailyAllowance = Number(
    (overnightStays === 0 ? baseDailyAllowanceByCountry / 2 : baseDailyAllowanceByCountry).toFixed(2)
  );
  const minDailyAllowance = standardDailyAllowance;
  const maxDailyAllowance = Number((standardDailyAllowance * 2).toFixed(2));

  const dailyAllowanceTooltip = useMemo(
    () => t('workTrips.dailyAllowanceRange', { min: minDailyAllowance, max: maxDailyAllowance }),
    [t, minDailyAllowance, maxDailyAllowance]
  );

  const accommodationLimitByCountry = Number(selectedCountryData?.accommodation_limit_eur || 0);

  const accommodationAmountTooltip = useMemo(
    () => t('workTrips.accommodationAmountRange', { max: accommodationLimitByCountry }),
    [t, accommodationLimitByCountry]
  );

  useEffect(() => {
    if (!bgCountry) return;

    if (tripScope === 'foreign') {
      if (selectedCountry?.value === bgCountry.id) {
        methods.setValue('country', null, { shouldValidate: false });
        methods.clearErrors('country');
      }
      return;
    }

    if (selectedCountry?.value !== bgCountry.id) {
      methods.setValue(
        'country',
        {
          value: bgCountry.id,
          label: bgCountry.name,
        },
        { shouldValidate: true }
      );
    }
  }, [bgCountry, tripScope, selectedCountry, methods]);

  useEffect(() => {
    if (!companyCar) return;

    methods.setValue(
      'transport_type',
      {
        value: companyCar.value,
        label: companyCar.label,
      },
      { shouldValidate: true }
    );
  }, [companyCar, methods]);

  useEffect(() => {
    methods.setValue('trip_days', tripDays, { shouldValidate: true });

    const defaultOvernights = Math.max(tripDays - 1, 0);
    methods.setValue('number_of_overnight_stays', defaultOvernights, { shouldValidate: true });
  }, [tripDays, methods]);

  useEffect(() => {
    if (!isStandardDailyAllowance) return;
    methods.setValue('daily_allowance', standardDailyAllowance, { shouldValidate: true });
  }, [isStandardDailyAllowance, standardDailyAllowance, methods]);

  useEffect(() => {
    if (!daylyAllowancePaymentTypeOptions.length) return;
    const defaultBank = daylyAllowancePaymentTypeOptions.find(option => option.value === 'bank');
    if (defaultBank) {
      methods.setValue('daily_allowance_payment_type', String(defaultBank.value), { shouldValidate: true });
    }
  }, [daylyAllowancePaymentTypeOptions, methods]);

  useEffect(() => {
    if (!accommodationOvernightStaysTypeOptions.length) return;
    const targetValue =
      overnightStays > 0
        ? accommodationOvernightStaysTypeValues.default
        : accommodationOvernightStaysTypeValues.noOvernightStays;
    const defaultOption = accommodationOvernightStaysTypeOptions.find(option => option.value === targetValue);
    if (defaultOption) {
      methods.setValue('accommodation_overnight_stays_type', String(defaultOption.value), { shouldValidate: true });
    }
  }, [overnightStays, accommodationOvernightStaysTypeOptions, accommodationOvernightStaysTypeValues, methods]);

  useEffect(() => {
    if (!accommodationPaymentTypeOptions.length) return;
    const defaultBank = accommodationPaymentTypeOptions.find(option => option.value === 'bank');
    if (defaultBank) {
      methods.setValue('accommodation_payment_type', String(defaultBank.value), { shouldValidate: true });
    }
  }, [accommodationPaymentTypeOptions, methods]);

  useEffect(() => {
    if (!travelPaymentTypeOptions.length) return;
    const defaultBank = travelPaymentTypeOptions.find(option => option.value === 'bank');
    if (defaultBank) {
      methods.setValue('travel_payment_type', String(defaultBank.value), { shouldValidate: true });
    }
  }, [travelPaymentTypeOptions, methods]);

  const tripScopeOptions = [
    {
      label: t('workTrips.domestic'),
      name: 'trip_scope',
      id: 'trip_scope_domestic',
      value: 'domestic',
    },
    {
      label: t('workTrips.foreign'),
      name: 'trip_scope',
      id: 'trip_scope_foreign',
      value: 'foreign',
    },
  ];

  const standardDailyAllowanceOptions = [
    {
      label: t('common.yes'),
      name: 'is_standard_daily_allowance',
      id: 'is_standard_daily_allowance_yes',
      value: 'yes',
    },
    {
      label: t('common.no'),
      name: 'is_standard_daily_allowance',
      id: 'is_standard_daily_allowance_no',
      value: 'no',
    },
  ];

  const toPaymentRadioOptions = (name: string, options: ISelectOptionProps[]) =>
    options.map(option => ({
      label: String(option.label),
      name,
      id: `${name}_${option.value}`,
      value: String(option.value),
      disabled:
        name === 'accommodation_overnight_stays_type' &&
        overnightStays === 0 &&
        option.value !== accommodationOvernightStaysTypeValues.noOvernightStays,
    }));

  const goToList = () => {
    const url = buildClientUrl('/work-trips', client);
    history.push(url);
  };

  return (
    <Row>
      <Col className="col-sm-8">
        <CreateEntity
          entity="work-trips"
          methods={methods}
          queryKey={buildClientEntity('work-trips', client)}
          onSuccess={() => {
            showModal({
              type: 'success',
              content: t('workTrips.createSuccess'),
            });
            methods.reset();
            goToList();
          }}
        >
          {(create: any) => (
            <Form
              title={t('workTrips.create')}
              methods={methods}
              btnLabel={t('common.create')}
              submitTestId="create-work-trip-button"
              primaryActionBtnType="primary"
              isUploading={isSubmitting}
              onSubmit={(data: IInputs) => {
                showModal({
                  type: 'confirm',
                  heading: t('common.confirm'),
                  content: t('workTrips.confirmCreate'),
                  onConfirm: async () => {
                    setIsSubmitting(true);

                    try {
                      const payload: any = {
                        user_id: Number(data.user_id?.value),
                        from_date: data.from_date ? format(data.from_date, 'yyyy-MM-dd') : null,
                        to_date: data.to_date ? format(data.to_date, 'yyyy-MM-dd') : null,
                        country: data.country?.value,
                        destination: data.destination,
                        task: data.task,
                        number_of_overnight_stays: Number(data.number_of_overnight_stays || 0),
                        daily_allowance: Number(data.daily_allowance || 0),
                        daily_allowance_payment_type: data.daily_allowance_payment_type,
                        travel_payment_type: data.travel_payment_type,
                        travel_amount: Number(data.travel_amount || 0),
                        transport_type: data.transport_type?.value,
                        transport_additional_details: data.transport_additional_details,
                        accommodation_amount: Number(data.accommodation_amount || 0),
                        accommodation_overnight_stays_type:
                          data.accommodation_overnight_stays_type ||
                          (data.trip_scope === 'foreign'
                            ? ACCOMMODATION_OVERNIGHT_STAYS_TYPE_VALUES.abroad.noOvernightStays
                            : ACCOMMODATION_OVERNIGHT_STAYS_TYPE_VALUES.domestic.noOvernightStays),
                      };

                      if (Number(data.number_of_overnight_stays || 0) > 0) {
                        payload.accommodation_payment_type = data.accommodation_payment_type;
                      }

                      if (data.trip_scope === 'foreign') {
                        payload.route = data.route;
                      }

                      await create(payload);
                    } catch (res: any) {
                      const error = createErrorFromResponse(res);
                      showModal({
                        type: 'error',
                        content: error.message,
                      });
                    } finally {
                      setIsSubmitting(false);
                    }
                  },
                });
              }}
            >
              <Row>
                <Col
                  sm={12}
                  md={4}
                >
                  <FormGroup
                    controlId="formWorkTripsUser"
                    label={t('workTrips.user')}
                    type="select"
                    as="select"
                    name="user_id"
                    isRequired
                    isSearchable
                    selectOptions={(dropdowns?.user || []) as any}
                    testId="work-trip-user-select"
                  />
                </Col>
                <Col
                  sm={6}
                  md={4}
                >
                  <FormGroup
                    controlId="formWorkTripsScope"
                    label={t('workTrips.tripIn')}
                    checkType="radio"
                    name="trip_scope"
                    options={tripScopeOptions}
                  />
                </Col>
                <Col
                  sm={6}
                  md={4}
                >
                  <FormGroup
                    controlId="formWorkTripsCountry"
                    label={t('workTrips.country')}
                    type="select"
                    as="select"
                    name="country"
                    isRequired
                    isDisabled={tripScope !== 'foreign'}
                    selectOptions={countryOptions as any}
                    testId="work-trip-country-select"
                  />
                </Col>
              </Row>

              <FormGroup
                controlId="formWorkTripsDestination"
                label={tripScope === 'foreign' ? t('workTrips.city') : t('workTrips.destination')}
                type="text"
                name="destination"
                isRequired
              />

              {tripScope === 'foreign' && (
                <FormGroup
                  controlId="formWorkTripsRoute"
                  label={t('workTrips.route')}
                  type="text"
                  name="route"
                  isRequired
                />
              )}

              <FormGroup
                controlId="formWorkTripsTask"
                label={t('workTrips.task')}
                type="textarea"
                name="task"
                isRequired
              />

              <Row>
                <Col
                  sm={6}
                  md={3}
                >
                  <FormGroup
                    controlId="formWorkTripsFromDate"
                    label={t('workTrips.fromDate')}
                    type="datepicker"
                    name="from_date"
                    isRequired
                    dateFormat="dd.MM.yyyy"
                    minDate={new Date()}
                    scrollableYearDropdown
                    showMonthDropdown
                    showYearDropdown
                    testId="work-trip-from-date"
                  />
                </Col>
                <Col
                  sm={6}
                  md={3}
                >
                  <FormGroup
                    controlId="formWorkTripsToDate"
                    label={t('workTrips.toDate')}
                    type="datepicker"
                    name="to_date"
                    isRequired
                    dateFormat="dd.MM.yyyy"
                    minDate={fromDate || new Date()}
                    scrollableYearDropdown
                    showMonthDropdown
                    showYearDropdown
                    testId="work-trip-to-date"
                  />
                </Col>
                <Col
                  sm={6}
                  md={3}
                >
                  <FormGroup
                    controlId="formWorkTripsTripDays"
                    label={t('workTrips.daysCount')}
                    type="number"
                    name="trip_days"
                    isDisabled
                    isNumber
                    min={0}
                  />
                </Col>
                <Col
                  sm={6}
                  md={3}
                >
                  <FormGroup
                    controlId="formWorkTripsOvernights"
                    label={t('workTrips.overnightStays')}
                    type="number"
                    name="number_of_overnight_stays"
                    isRequired
                    isNumber
                    isWholeNumber
                    min={0}
                    max={Math.max(tripDays, 0)}
                  />
                </Col>
              </Row>

              <Row>
                <Col sm={6}>
                  <FormGroup
                    controlId="formWorkTripsDailyAllowanceMode"
                    label={t('workTrips.higherDailyAllowance')}
                    checkType="radio"
                    name="is_standard_daily_allowance"
                    options={standardDailyAllowanceOptions}
                  />
                </Col>
                <Col sm={6}>
                  <FormGroup
                    controlId="formWorkTripsDailyAllowance"
                    label={t('workTrips.dailyAllowance')}
                    type="number"
                    name="daily_allowance"
                    isDisabled={isStandardDailyAllowance}
                    isNumber
                    min={minDailyAllowance}
                    max={maxDailyAllowance}
                    hint={!isStandardDailyAllowance ? dailyAllowanceTooltip : undefined}
                  />
                </Col>
              </Row>

              <FormGroup
                controlId="formWorkTripsTransport"
                label={t('workTrips.transportType')}
                type="select"
                as="select"
                name="transport_type"
                isRequired
                selectOptions={transportOptions as any}
              />

              <FormGroup
                controlId="formWorkTripsTransportDetails"
                label={t('workTrips.transportDetails')}
                type="textarea"
                name="transport_additional_details"
              />

              <FormGroup
                controlId="formWorkTripsDailyAllowancePaymentType"
                label={t('workTrips.dailyAllowancePaymentType')}
                checkType="radio"
                name="daily_allowance_payment_type"
                options={toPaymentRadioOptions('daily_allowance_payment_type', daylyAllowancePaymentTypeOptions)}
                isRequired
              />

              {tripDays > 0 && (
                <Row>
                  <Col
                    sm={6}
                    md={4}
                  >
                    <FormGroup
                      controlId="formWorkTripsAccommodationOvernightStaysType"
                      label={t('workTrips.accommodationOvernightStaysType')}
                      checkType="radio"
                      name="accommodation_overnight_stays_type"
                      options={toPaymentRadioOptions(
                        'accommodation_overnight_stays_type',
                        accommodationOvernightStaysTypeOptions
                      )}
                      isRequired
                    />
                  </Col>
                  {overnightStays > 0 &&
                    (accommodationOvernightStaysType === accommodationOvernightStaysTypeValues.documented ||
                      accommodationOvernightStaysType ===
                        accommodationOvernightStaysTypeValues.documentedWithLimit) && (
                      <Col
                        sm={6}
                        md={4}
                      >
                        <FormGroup
                          controlId="formWorkTripsAccommodationPaymentType"
                          label={t('workTrips.accommodationPaymentType')}
                          checkType="radio"
                          name="accommodation_payment_type"
                          options={toPaymentRadioOptions('accommodation_payment_type', accommodationPaymentTypeOptions)}
                          isRequired
                        />
                      </Col>
                    )}
                  {overnightStays > 0 &&
                    accommodationOvernightStaysType === accommodationOvernightStaysTypeValues.documentedWithLimit && (
                      <Col
                        sm={6}
                        md={4}
                      >
                        <FormGroup
                          controlId="formWorkTripsAccommodationAmount"
                          label={t('workTrips.accommodationAmount')}
                          type="number"
                          name="accommodation_amount"
                          isNumber
                          min={0}
                          max={tripScope === 'foreign' ? accommodationLimitByCountry : undefined}
                          hint={tripScope === 'foreign' ? accommodationAmountTooltip : undefined}
                        />
                      </Col>
                    )}
                </Row>
              )}

              <Row>
                <Col sm={6}>
                  <FormGroup
                    controlId="formWorkTripsTravelPaymentType"
                    label={t('workTrips.travelPaymentType')}
                    checkType="radio"
                    name="travel_payment_type"
                    options={toPaymentRadioOptions('travel_payment_type', travelPaymentTypeOptions)}
                    isRequired
                  />
                </Col>
                <Col sm={6}>
                  <FormGroup
                    controlId="formWorkTripsTravelAmount"
                    label={t('workTrips.travelAmount')}
                    type="number"
                    name="travel_amount"
                    isNumber
                    min={0}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </CreateEntity>
      </Col>
    </Row>
  );
};

export default CreateForm;
